import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowRight, ExternalLink } from 'lucide-react';
import { Badge } from './ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const NewsSection = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.blog}?limit=6`);
        setArticles(response.data.slice(0, 6));
      } catch (err) {
        console.error('Failed to fetch news:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleArticleClick = (article) => {
    if (article.external_url) {
      window.open(article.external_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/actualites/${article.slug}`);
    }
  };

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
        </div>
      </section>
    );
  }

  const featured = articles[0];
  const rest = articles.slice(1, 4);

  return (
    <section className="py-24 bg-white" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-3 font-sans">
              Actualites & Evenements
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-navy tracking-tight">
              Nos Dernieres Actualites
            </h2>
          </div>
          <button
            onClick={() => navigate('/actualites')}
            data-testid="view-all-news-btn"
            className="inline-flex items-center text-navy font-semibold text-sm hover:text-forest transition-colors group font-sans"
          >
            Voir toutes les actualites
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {featured && (
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Featured Article */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group cursor-pointer rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all duration-300"
              onClick={() => handleArticleClick(featured)}
              data-testid="home-article-0"
            >
              <div className="relative h-72 lg:h-full min-h-[280px] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-forest text-white text-xs px-3 py-1">{featured.category}</Badge>
                </div>
                {featured.external_url && (
                  <div className="absolute top-4 right-4 bg-white/90 rounded-full p-1.5">
                    <ExternalLink className="w-4 h-4 text-navy" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center text-sm text-white/80 mb-2 font-sans">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{formatDate(featured.created_at)}</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-white leading-tight">{featured.title}</h3>
                </div>
              </div>
            </motion.div>

            {/* Remaining articles */}
            <div className="flex flex-col gap-4">
              {rest.map((article, idx) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="group cursor-pointer flex gap-4 rounded-2xl border border-black/5 p-3 hover:shadow-md transition-all duration-300"
                  onClick={() => handleArticleClick(article)}
                  data-testid={`home-article-${idx + 1}`}
                >
                  <div className="w-32 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs text-forest border-forest/30 px-2 py-0.5">{article.category}</Badge>
                      {article.external_url && <ExternalLink className="w-3 h-3 text-gray-400" />}
                    </div>
                    <h4 className="font-serif font-bold text-navy text-base leading-snug line-clamp-2 group-hover:text-forest transition-colors">
                      {article.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1 font-sans">{formatDate(article.created_at)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
